function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import CloudWatchLogs from "aws-sdk/clients/cloudwatchlogs";
export default class Logger {
  /**
   * Constructor.
   *
   * @param accessKeyId     - AWS Access Key ID
   * @param secretAccessKey - AWS Secret Access Key
   * @param region          - AWS Region (e.g. ap-northeast-1)
   * @param logGroupName    - AWS CloudWatch Log Group Name
   */
  constructor(accessKeyId, secretAccessKey, region, logGroupName) {
    this.accessKeyId = accessKeyId;
    this.secretAccessKey = secretAccessKey;
    this.region = region;
    this.logGroupName = logGroupName;

    _defineProperty(this, "levels", ["error"]);

    _defineProperty(this, "interval", 10000);

    _defineProperty(this, "muting", false);

    _defineProperty(this, "enabled", true);

    _defineProperty(this, "logStreamNameResolver", void 0);

    _defineProperty(this, "messageFormatter", void 0);

    _defineProperty(this, "client", void 0);

    _defineProperty(this, "storage", void 0);

    _defineProperty(this, "console", void 0);

    _defineProperty(this, "events", []);

    _defineProperty(this, "intervalId", void 0);
  }
  /**
   * Set level.
   *
   * @param levels - Reported error level
   */


  setLevels(levels) {
    this.levels = levels;
    return this;
  }
  /**
   * Set interval.
   *
   * @param interval - Interval milliseconds for sending logs
   */


  setInterval(interval) {
    this.interval = interval;
    return this;
  }
  /**
   * Mute logging in browser console.
   */


  mute() {
    this.muting = true;
    return this;
  }
  /**
   * Resume logging in browser console.
   */


  unmute() {
    this.muting = false;
    return this;
  }
  /**
   * Enable collecting errors and sending to AWS CloudWatch.
   */


  enable() {
    this.enabled = true;
    return this;
  }
  /**
   * Disable collecting errors and sending to AWS CloudWatch.
   */


  disable() {
    this.enabled = false;
    return this;
  }
  /**
   * Bootstrap Logger.
   *
   * @param logStreamNameResolver - Resolve logStreamName for current user (e.g. Canvas Fingerprint)
   * @param messageFormatter      - Format message string from Error
   * @param Ctor
   * @param storage
   * @param globalConsole
   * @param eventTarget
   */


  install({
    logStreamNameResolver,
    messageFormatter,
    ClientConstructor: Ctor = CloudWatchLogs,
    storage = localStorage,
    console: globalConsole = console,
    eventTarget = window
  } = {}) {
    this.client = new Ctor({
      accessKeyId: this.accessKeyId,
      secretAccessKey: this.secretAccessKey,
      region: this.region
    });
    this.logStreamNameResolver = logStreamNameResolver;
    this.messageFormatter = messageFormatter;
    this.storage = storage; // Swap window.console.*() functions and overridden ones

    const originalConsole = {};

    for (const level of this.levels) {
      originalConsole[level] = globalConsole[level].bind(globalConsole);

      globalConsole[level] = async (message, ...args) => {
        // Listen overridden console.*() function calls (type="console", level="*")
        await this.onError(new Error(message), {
          type: "console",
          level
        });

        if (!this.muting) {
          originalConsole[level](message, ...args);
        }
      };
    }

    this.console = originalConsole; // Listen "error" event on window (type="uncaught")

    eventTarget.addEventListener("error", async error => {
      await this.onError(error, {
        type: "uncaught"
      });
    }); // Start timer that executes this.onInterval()

    this.intervalId = setInterval(this.onInterval.bind(this), this.interval);
  }
  /**
   * Queue a new error.
   *
   * @param e    - Error object
   * @param info - Extra Error Info (Consider using "type" field)
   */


  async onError(e, info) {
    if (!Logger.isValidError(e) || !this.enabled) {
      return;
    }

    const message = this.messageFormatter ? await this.messageFormatter(e, info) // Custom formatter
    : JSON.stringify({
      message: e.message,
      ...info
    }); // Simple JSON formatter
    // Abort when received null

    if (!message) {
      return;
    }

    this.events.push({
      timestamp: new Date().getTime(),
      message
    });
  }
  /**
   * Send queued errors.
   */


  async onInterval() {
    if (!this.enabled) {
      return;
    } // Extract errors from queue


    const pendingEvents = this.events.splice(0);

    if (!pendingEvents.length) {
      return;
    } // Retrieve or newly calculate logStreamName for current user


    const logStreamName = await this.getLogStreamName();

    if (!logStreamName) {
      return;
    } // Retrieve previous "nextSequenceToken" from cache


    const sequenceToken = await this.getCache("sequenceToken"); // Build parameters for PutLogEvents endpoint
    //   c.f. https://docs.aws.amazon.com/AmazonCloudWatchLogs/latest/APIReference/API_PutLogEvents.html

    const params = {
      logEvents: pendingEvents,
      logGroupName: this.logGroupName,
      logStreamName: logStreamName,
      ...(sequenceToken ? {
        sequenceToken
      } : undefined)
    };
    let nextSequenceToken = undefined,
        match = null;

    try {
      // Run request to send events and retrieve fresh "nextSequenceToken"
      ({
        nextSequenceToken = undefined
      } = await new Promise((resolve, reject) => {
        this.getClient().putLogEvents(params, (err, data) => err ? reject(err) : resolve(data));
      }));
    } catch (e) {
      // Try to recover from InvalidSequenceTokenException error message
      if (!Logger.isValidError(e) || e.code !== "InvalidSequenceTokenException" || !(match = e.message.match(/The next expected sequenceToken is: (\w+)/))) {
        // Print error to original console and reset states
        this.getConsole().error(e);
        await this.refresh();
        return;
      }
    } // Recover from InvalidSequenceTokenException error message


    if (match) {
      nextSequenceToken = match[1];
    } // Cache fresh "nextSequenceToken"


    if (nextSequenceToken) {
      await this.setCache("sequenceToken", nextSequenceToken);
    } // Immediately retry after recovery


    if (match) {
      this.events.push(...pendingEvents);
      setTimeout(this.onInterval, 0);
    }
  }

  getClient() {
    if (!this.client) {
      throw new Error("Not yet installed");
    }

    return this.client;
  }

  getStorage() {
    if (!this.storage) {
      throw new Error("Not yet installed");
    }

    return this.storage;
  }

  getConsole() {
    if (!this.console) {
      throw new Error("Not yet installed");
    }

    return this.console;
  }

  async setCache(key, value) {
    return this.getStorage().setItem(`${Logger.namespace}:${key}`, value);
  }

  async getCache(key) {
    return this.getStorage().getItem(`${Logger.namespace}:${key}`);
  }

  async deleteCache(key) {
    return this.getStorage().removeItem(`${Logger.namespace}:${key}`);
  }

  async refresh() {
    await this.deleteCache("logStreamName");
    await this.deleteCache("sequenceToken");
    this.events.splice(0);
  }

  async getLogStreamName() {
    // Retrieve "logStreamName" for current user
    const retrieved = await this.getCache("logStreamName");

    if (retrieved) {
      return retrieved;
    } // Build parameters for CreateLogStream endpoint
    //   c.f. https://docs.aws.amazon.com/AmazonCloudWatchLogs/latest/APIReference/API_CreateLogStream.html


    const params = {
      logGroupName: this.logGroupName,
      logStreamName: this.logStreamNameResolver && (await this.logStreamNameResolver()) || // Resolve for current user (e.g. Canvas Fingerprint)
      Logger.defaultLogStreamName // "anonymous"

    };

    try {
      // Run request to create a new logStream
      await new Promise((resolve, reject) => {
        this.getClient().createLogStream(params, (err, data) => err ? reject(err) : resolve(data));
      });
    } catch (e) {
      // Try to recover from ResourceAlreadyExistsException error
      if (!Logger.isValidError(e) || e.code !== "ResourceAlreadyExistsException") {
        // Print error to original console and reset states
        this.getConsole().error(e);
        await this.refresh();
        return null;
      }
    } // Cache fresh "logStreamName"


    await this.setCache("logStreamName", params.logStreamName);
    return params.logStreamName;
  }

  static isValidError(value) {
    return value && typeof value.message === "string";
  }

}

_defineProperty(Logger, "namespace", "CloudWatchFrontLogger");

_defineProperty(Logger, "defaultLogStreamName", "anonymous");